import { memo } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { Badge } from '@progress/kendo-react-indicators';
import { Switch } from '@progress/kendo-react-inputs';

import { formatNotificationTime } from '@shared/utils/formatNotificationTime';
import classes from './NotificationMenu.module.scss';

export interface NotificationMenuItem {
  id: string | number;
  message: string;
  // null when the API can't find the creator
  authorName?: string | null;
  createdAt: Date;
  unread: boolean;
}

interface NotificationMenuProps {
  items: NotificationMenuItem[];
  unreadCount: number;
  onlyUnread: boolean;
  onToggleOnlyUnread: (onlyUnread: boolean) => void;
  onMarkAllRead: () => void;
  onItemClick?: (item: NotificationMenuItem) => void;
  onViewAll?: () => void;
}

const initialsOf = (name?: string | null): string => {
  const [ first = '', second = '' ] = (name ?? '').split(' ');
  return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase();
};

const NotificationMenu = ({
  items,
  unreadCount,
  onlyUnread,
  onToggleOnlyUnread,
  onMarkAllRead,
  onItemClick,
  onViewAll,
}: NotificationMenuProps): JSX.Element => (
  <div className={classes.notificationMenu}>
    <Button fillMode="link" className="bell" type="button" aria-label="Notifications">
      {unreadCount > 0 && <Badge themeColor="error">{unreadCount <= 9 ? unreadCount : '9+'}</Badge>}
    </Button>
    <div className="panel">
      <div className="heading">
        <h5>Notifications</h5>
        <div className="unreadToggle">
          <Switch checked={onlyUnread} onChange={() => onToggleOnlyUnread(!onlyUnread)} />
          <label>Unread Only</label>
        </div>
        <Button fillMode="link" className="markAll" type="button" disabled={unreadCount === 0} onClick={onMarkAllRead}>
          Mark all as read
        </Button>
      </div>
      <ul className="list">
        {items.map((item) => (
          <li key={item.id} className="item" onClick={onItemClick && (() => onItemClick(item))}>
            <div className="avatar">
              {item.unread && <Badge />}
              <span className="initials">{initialsOf(item.authorName)}</span>
            </div>
            <div className="summary">
              <h6>{item.message}</h6>
              <span className="timestamp">{formatNotificationTime(item.createdAt)}</span>
            </div>
          </li>
        ))}
      </ul>
      <div className="viewAll">
        <Button fillMode="link" type="button" disabled={!onViewAll} onClick={onViewAll}>
          View All Notifications
        </Button>
      </div>
    </div>
  </div>
);

export default memo(NotificationMenu);
